import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import { UKFlag } from './HeroSection';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/63xg2u2p_WhatsApp%20Image%202026-03-14%20at%2018.12.05.jpeg";

const content = {
  en: {
    tagline: 'Global Action. British Trust.',
    about: 'HYDRO LIFE Foundation is a UK-registered charity bringing clean water to communities worldwide.',
    links: ['Mission', 'Technology', 'Impact', 'Donate'],
    contact: {
      title: 'Contact',
      location: '2165 Van Buren ST 911, Hollywood, Florida, USA',
      email: 'contacto@hidrolife.org',
      email2: 'hidrolife.foundation.uk@gmail.com',
    },
    legal: ['Privacy Policy', 'Terms', 'Cookies'],
    copyright: '© 2024 HYDRO LIFE Foundation. All rights reserved.',
    owner: 'Hidrolife.org is owned and operated by PAPES US LLC.',
    address: 'Address: 2165 Van Buren ST 911, Hollywood, Florida, USA.',
  },
  es: {
    tagline: 'Acción Global. Confianza Británica.',
    about: 'HYDRO LIFE Foundation es una caridad registrada en UK que lleva agua limpia a comunidades en todo el mundo.',
    links: ['Misión', 'Tecnología', 'Impacto', 'Donar'],
    contact: {
      title: 'Contacto',
      location: '2165 Van Buren ST 911, Hollywood, Florida, USA',
      email: 'contacto@hidrolife.org',
      email2: 'hidrolife.foundation.uk@gmail.com',
    },
    legal: ['Privacidad', 'Términos', 'Cookies'],
    copyright: '© 2024 HYDRO LIFE Foundation. Todos los derechos reservados.',
    owner: 'Hidrolife.org es propiedad y está operado por PAPES US LLC.',
    address: 'Dirección: 2165 Van Buren ST 911, Hollywood, Florida, USA.',
  },
};

export const Footer = ({ language }) => {
  const t = content[language];

  return (
    <footer className="relative overflow-hidden" data-testid="footer">
      {/* Seamless gradient from previous section */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F3A] to-[#050D18]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          
          {/* Brand */}
          <div className="md:col-span-1">
            {/* Circular Logo */}
            <motion.div 
              className="w-16 h-16 rounded-full overflow-hidden border-2 border-rose-500/30 mb-4"
              animate={{
                boxShadow: [
                  '0 0 15px rgba(244, 114, 182, 0.2)',
                  '0 0 25px rgba(244, 114, 182, 0.4)',
                  '0 0 15px rgba(244, 114, 182, 0.2)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <img src={LOGO_URL} alt="HYDRO LIFE" className="w-full h-full object-cover" />
            </motion.div>
            
            <div className="flex items-center gap-2 mb-3">
              <UKFlag className="w-6 h-4" />
              <span className="text-xs text-rose-300/80">UK Registered</span>
            </div>
            
            <p className="text-sm text-white/40 leading-relaxed mb-3">
              {t.about}
            </p>
            <p className="text-rose-400/80 font-medium text-sm">{t.tagline}</p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-sm font-medium text-white/80 mb-4">Links</h4>
            <ul className="space-y-2">
              {t.links.map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase()}`} className="text-sm text-white/40 hover:text-rose-300 transition-colors">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-medium text-white/80 mb-4">{t.contact.title}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-white/40">
                <MapPin className="w-4 h-4 text-rose-400/70 mt-0.5 flex-shrink-0" />
                <span>{t.contact.location}</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-white/40">
                <Mail className="w-4 h-4 text-rose-400/70 flex-shrink-0" />
                <a href="mailto:contacto@hidrolife.org" className="hover:text-rose-300 transition-colors">
                  {t.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-white/40">
                <Mail className="w-4 h-4 text-cyan-400/70 flex-shrink-0" />
                <a href="mailto:hidrolife.foundation.uk@gmail.com" className="hover:text-cyan-300 transition-colors">
                  {t.contact.email2}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-medium text-white/80 mb-4">Legal</h4>
            <ul className="space-y-2">
              {t.legal.map((link) => (
                <li key={link}>
                  <a href="#" className="text-sm text-white/40 hover:text-rose-300 transition-colors">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Legal Ownership Section */}
        <div className="py-6 border-t border-white/5">
          <div className="text-center space-y-2">
            <p className="text-sm text-white/50 font-medium">
              {t.owner}
            </p>
            <p className="text-xs text-white/40">
              {t.address}
            </p>
            <p className="text-xs text-white/40">
              Contact: <a href="mailto:contacto@hidrolife.org" className="text-rose-400/70 hover:text-rose-300">contacto@hidrolife.org</a> / <a href="mailto:hidrolife.foundation.uk@gmail.com" className="text-cyan-400/70 hover:text-cyan-300">hidrolife.foundation.uk@gmail.com</a>
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/30">{t.copyright}</p>
            <div className="flex items-center gap-2">
              <UKFlag className="w-5 h-3 opacity-50" />
              <span className="text-xs text-rose-400/60">UK Charity Registration in Progress</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
